import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle } from '../theme';
import { TrendingUp } from 'lucide-react';

// ProfitabilityReportPage — by-treatment-type revenue + margin (Phase 2 / item #35).
export default function ProfitabilityReportPage({ API_BASE }) {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState(new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10));
  const [to,   setTo]   = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/profitability/by-treatment-type`, { params: { from, to } });
      setData(r.data);
    } catch { setData(null); }
  }, [API_BASE, from, to]);

  useEffect(() => { load(); }, [load]);

  const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, color: 'var(--color-text)' }}><TrendingUp size={20} style={{ verticalAlign: 'middle' }}/> Treatment Profitability</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ ...inputStyle, width: 'auto' }}/>
          <input type="date" value={to}   onChange={e => setTo(e.target.value)}   style={{ ...inputStyle, width: 'auto' }}/>
          <button onClick={load} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: theme.primary, color: 'white', fontWeight: 600 }}>Run</button>
        </div>
      </div>

      {!data ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading…</div> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 14 }}>
            <Stat label="Treatments Done" value={data.totals.count} />
            <Stat label="Total Revenue"   value={fmt(data.totals.revenue)} color={theme.success}/>
            <Stat label="Total Cost"      value={fmt(data.totals.cost)}    color={theme.warning}/>
            <Stat label="Margin"          value={fmt(data.totals.margin)}  color={theme.success}/>
            <Stat label="Margin %"        value={`${data.totals.marginPct}%`} color={theme.info}/>
          </div>

          <div style={{ ...cardStyle }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                <Th>Treatment</Th><Th>Count</Th><Th>Avg Price</Th><Th>Revenue</Th><Th>Est. Cost</Th><Th>Margin</Th><Th>Margin %</Th>
              </tr></thead>
              <tbody>
                {data.rows.map(r => (
                  <tr key={r.name} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <Td>{r.name}</Td>
                    <Td>{r.count}</Td>
                    <Td>{fmt(r.avgPrice)}</Td>
                    <Td>{fmt(r.revenue)}</Td>
                    <Td>{fmt(r.cost)}</Td>
                    <Td><span style={{ color: r.margin >= 0 ? theme.success : theme.danger, fontWeight: 700 }}>{fmt(r.margin)}</span></Td>
                    <Td>{r.marginPct}%</Td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No completed treatments in this window</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const Stat = ({ label, value, color = 'var(--color-text)' }) => (
  <div style={{ ...cardStyle, padding: 12 }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
  </div>
);
const Th = ({ children }) => (<th style={{ textAlign: 'left', padding: 8, fontSize: 11, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>{children}</th>);
const Td = ({ children }) => (<td style={{ padding: 8, color: 'var(--color-text)' }}>{children}</td>);
